import { useCallback, useEffect, useState } from "react";
import { Trash2 } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiError } from "../../lib/api";
import { logger } from "../../lib/logger";
import ScoreStepper from "../../components/ScoreStepper";
import { useI18n } from "../../i18n";
import { fmtTime } from "./utils";

export default function AdminPanel({ matches, teams, onReload, onRecalc, onDeletePool, t: tFromParent }) {
    const { t: tCtx, lang } = useI18n();
    const t = tFromParent || tCtx;
    const [syncing, setSyncing] = useState(false);
    const [editingMatch, setEditingMatch] = useState(null);
    const [hg, setHg] = useState(0);
    const [ag, setAg] = useState(0);
    const [penWinner, setPenWinner] = useState("");
    const [searchA, setSearchA] = useState("");
    const [schedulerStatus, setSchedulerStatus] = useState(null);
    const [runningNow, setRunningNow] = useState(false);
    const [runningLive, setRunningLive] = useState(false);

    const loadScheduler = useCallback(async () => {
        try {
            const { data } = await api.get("/admin/scheduler/status");
            setSchedulerStatus(data);
        } catch (e) {
            logger.warn("scheduler status failed", e);
        }
    }, []);
    useEffect(() => { loadScheduler(); }, [loadScheduler]);

    const runSchedulerNow = async () => {
        setRunningNow(true);
        try {
            const { data } = await api.post("/admin/scheduler/run-now");
            toast.success(t("admin.toast.synced_full", { updated: data.updated, changed: data.results_changed }));
            await loadScheduler();
            await onReload();
        } catch (e) {
            toast.error(formatApiError(e.response?.data?.detail) || e.message);
        } finally { setRunningNow(false); }
    };

    const runLiveNow = async () => {
        setRunningLive(true);
        try {
            const { data } = await api.post("/admin/scheduler/live-poll-now");
            if (data.skipped) {
                toast.info(t("admin.live.toast.skipped"));
            } else {
                toast.success(t("admin.live.toast.ok", { updated: data.updated, changed: data.results_changed }));
            }
            await loadScheduler();
            await onReload();
        } catch (e) {
            toast.error(formatApiError(e.response?.data?.detail) || e.message);
        } finally { setRunningLive(false); }
    };

    const sync = async (kind) => {
        setSyncing(true);
        try {
            const url = kind === "teams" ? "/admin/sync/teams" : "/admin/sync/fixtures";
            await api.post(url);
            toast.success(t("admin.toast.sync_ok", { kind }));
            await onReload();
        } catch (e) {
            toast.error(formatApiError(e.response?.data?.detail) || e.message);
        } finally { setSyncing(false); }
    };

    const openEdit = (m) => {
        setEditingMatch(m);
        setHg(m.home_goals ?? 0);
        setAg(m.away_goals ?? 0);
        setPenWinner(m.winner_team_id || "");
    };

    const saveResult = async () => {
        try {
            await api.post(`/admin/matches/${editingMatch.id}/result`, {
                home_goals: hg, away_goals: ag, status: "finished",
            });
            // Knockout matches that end level need a penalty winner so the
            // bracket can resolve them. Push the winner via the edit endpoint.
            if (editingMatch.stage === "knockout" && hg === ag) {
                await api.patch(`/admin/matches/${editingMatch.id}`, {
                    winner_team_id: penWinner || null,
                });
            }
            toast.success(t("admin.toast.result_saved"));
            setEditingMatch(null);
            await onReload();
        } catch (e) {
            toast.error(formatApiError(e.response?.data?.detail) || e.message);
        }
    };

    const filtered = matches.filter((m) => {
        if (!searchA) return true;
        const q = searchA.toLowerCase();
        return (teams[m.home_team_id]?.name?.toLowerCase().includes(q) ||
                teams[m.away_team_id]?.name?.toLowerCase().includes(q) ||
                m.round?.toLowerCase().includes(q));
    });

    return (
        <div data-testid="admin-view" className="space-y-6">
            {schedulerStatus && (
                <div className="space-y-3" data-testid="scheduler-panel">
                    {/* Fixtures sync (every 60 min) */}
                    <div className="p-4 rounded-lg border border-[var(--border-base)] bg-[var(--bg-card)]">
                        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
                            <div>
                                <div className="flex items-center gap-2">
                                    <span className={`h-2 w-2 rounded-full ${schedulerStatus.running ? "bg-[#00FF66] animate-pulse" : "bg-[#71717A]"}`} />
                                    <h3 className="font-heading text-lg font-bold uppercase tracking-tight text-[var(--text-primary)]">
                                        {t("admin.scheduler.fixtures.title", { n: schedulerStatus.interval_minutes })}
                                    </h3>
                                    <span className="text-[9px] font-extrabold uppercase tracking-widest px-1.5 py-0.5 rounded bg-[var(--accent-hex)] text-[var(--accent-text)]" data-testid="scheduler-state">
                                        {schedulerStatus.running ? t("admin.scheduler.on") : t("admin.scheduler.off")}
                                    </span>
                                </div>
                                <div className="text-xs text-[var(--text-secondary)] mt-1 space-y-0.5">
                                    <div>{t("admin.scheduler.next")} <span className="text-[var(--text-primary)] font-mono" data-testid="scheduler-next">{schedulerStatus.next_run ? new Date(schedulerStatus.next_run).toLocaleString(lang === "sv" ? "sv-SE" : undefined) : "—"}</span></div>
                                    {schedulerStatus.last_run && (
                                        <div data-testid="scheduler-last">
                                            {t("admin.scheduler.last")} <span className="text-[var(--text-primary)] font-mono">{new Date(schedulerStatus.last_run.at).toLocaleString(lang === "sv" ? "sv-SE" : undefined)}</span>
                                            {" · "}
                                            <span className={schedulerStatus.last_run.status === "ok" ? "text-[#00FF66]" : "text-[#FF3B30]"}>
                                                {schedulerStatus.last_run.status}
                                            </span>
                                            {schedulerStatus.last_run.status === "ok" && (
                                                <span className="text-[var(--text-secondary)]"> · {t("admin.scheduler.summary", { updated: schedulerStatus.last_run.updated, changed: schedulerStatus.last_run.results_changed })}</span>
                                            )}
                                        </div>
                                    )}
                                </div>
                            </div>
                            <div className="flex gap-2">
                                <button onClick={loadScheduler} data-testid="scheduler-refresh"
                                    className="text-xs uppercase tracking-widest font-bold text-[var(--text-secondary)] hover:text-[var(--text-primary)] px-3 py-2 border border-[var(--border-base)] rounded-md">{t("admin.scheduler.refresh")}</button>
                                <button onClick={runSchedulerNow} disabled={runningNow} data-testid="scheduler-run-now"
                                    className="text-xs uppercase tracking-widest font-extrabold text-[var(--accent-text)] bg-[var(--accent-hex)] hover:bg-[var(--accent-hex-hover)] disabled:opacity-50 px-3 py-2 rounded-md">
                                    {runningNow ? t("admin.scheduler.syncing") : t("admin.scheduler.run_now")}
                                </button>
                            </div>
                        </div>
                    </div>

                    {/* Live poll (every 2 min, gated by match window) */}
                    {schedulerStatus.live && (
                        <div className={`p-4 rounded-lg border ${schedulerStatus.live.in_window ? "border-[var(--accent-border)] bg-[var(--accent-hex)]/5" : "border-[var(--border-base)] bg-[var(--bg-card)]"}`}>
                            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
                                <div>
                                    <div className="flex items-center gap-2">
                                        <span className={`h-2 w-2 rounded-full ${schedulerStatus.live.in_window ? "bg-[#FF3B30] animate-pulse" : "bg-[#71717A]"}`} />
                                        <h3 className="font-heading text-lg font-bold uppercase tracking-tight text-[var(--text-primary)]">
                                            {t("admin.live.title", { n: schedulerStatus.live.interval_minutes })}
                                        </h3>
                                        <span className={`text-[9px] font-extrabold uppercase tracking-widest px-1.5 py-0.5 rounded ${
                                            schedulerStatus.live.in_window ? "bg-[#FF3B30] text-[var(--text-primary)]" : "bg-[var(--overlay-light)] text-[var(--text-secondary)]"
                                        }`} data-testid="live-state">
                                            {schedulerStatus.live.in_window ? t("admin.live.match_live") : t("admin.live.idle")}
                                        </span>
                                    </div>
                                    <div className="text-xs text-[var(--text-secondary)] mt-1 space-y-0.5">
                                        <div>{schedulerStatus.live.in_window ? t("admin.live.polling_desc") : t("admin.live.skip_desc")}</div>
                                        <div>{t("admin.live.next_check")} <span className="text-[var(--text-primary)] font-mono" data-testid="live-next">{schedulerStatus.live.next_run ? new Date(schedulerStatus.live.next_run).toLocaleString(lang === "sv" ? "sv-SE" : undefined) : "—"}</span></div>
                                        {schedulerStatus.live.last_run && (
                                            <div data-testid="live-last">
                                                {t("admin.live.last")} <span className="text-[var(--text-primary)] font-mono">{new Date(schedulerStatus.live.last_run.at).toLocaleString(lang === "sv" ? "sv-SE" : undefined)}</span>
                                                {" · "}
                                                <span className={
                                                    schedulerStatus.live.last_run.status === "ok" ? "text-[#00FF66]" :
                                                    schedulerStatus.live.last_run.status === "skipped" ? "text-[var(--text-secondary)]" :
                                                    "text-[#FF3B30]"
                                                }>{schedulerStatus.live.last_run.status}</span>
                                                {schedulerStatus.live.last_run.status === "ok" && (
                                                    <span className="text-[var(--text-secondary)]"> · {t("admin.live.summary", { updated: schedulerStatus.live.last_run.updated, changed: schedulerStatus.live.last_run.results_changed })}</span>
                                                )}
                                            </div>
                                        )}
                                    </div>
                                </div>
                                <button onClick={runLiveNow} disabled={runningLive} data-testid="live-run-now"
                                    className="text-xs uppercase tracking-widest font-extrabold text-[var(--text-primary)] border border-[var(--border-hover)] hover:border-white/60 disabled:opacity-50 px-3 py-2 rounded-md">
                                    {runningLive ? t("admin.live.polling") : t("admin.live.poll_now")}
                                </button>
                            </div>
                        </div>
                    )}
                </div>
            )}

            <div className="grid sm:grid-cols-3 gap-3">
                <button onClick={() => sync("teams")} disabled={syncing} data-testid="admin-sync-teams"
                    className="p-4 rounded-lg border border-[var(--border-base)] bg-[var(--bg-card)] hover:border-[var(--accent-border)] transition-colors text-left disabled:opacity-50">
                    <div className="font-heading text-lg font-bold uppercase text-[var(--text-primary)]">{t("admin.sync.teams.title")}</div>
                    <p className="text-xs text-[var(--text-secondary)] mt-1">{t("admin.sync.teams.desc")}</p>
                </button>
                <button onClick={() => sync("fixtures")} disabled={syncing} data-testid="admin-sync-fixtures"
                    className="p-4 rounded-lg border border-[var(--border-base)] bg-[var(--bg-card)] hover:border-[var(--accent-border)] transition-colors text-left disabled:opacity-50">
                    <div className="font-heading text-lg font-bold uppercase text-[var(--text-primary)]">{t("admin.sync.fixtures.title")}</div>
                    <p className="text-xs text-[var(--text-secondary)] mt-1">{t("admin.sync.fixtures.desc")}</p>
                </button>
                <button onClick={onRecalc} data-testid="admin-recalc"
                    className="p-4 rounded-lg border border-[var(--accent-border)] bg-[var(--accent-hex)]/5 hover:bg-[var(--accent-hex)]/10 transition-colors text-left">
                    <div className="font-heading text-lg font-bold uppercase text-[var(--accent-hex)]">{t("admin.recalc.title")}</div>
                    <p className="text-xs text-[var(--text-secondary)] mt-1">{t("admin.recalc.desc")}</p>
                </button>
            </div>

            <div>
                <div className="flex items-center justify-between mb-3">
                    <h3 className="font-heading text-xl font-bold uppercase text-[var(--text-primary)]">{t("admin.edit.title")}</h3>
                    <input value={searchA} onChange={(e) => setSearchA(e.target.value)} placeholder={t("admin.search")}
                        data-testid="admin-search"
                        className="bg-[var(--bg-card)] border border-[var(--border-base)] text-[var(--text-primary)] px-3 py-2 rounded-md text-sm focus:outline-none focus:border-[#CCFF00] w-56" />
                </div>

                <div className="rounded-lg border border-[var(--border-base)] overflow-hidden">
                    {filtered.slice(0, 80).map((m) => (
                        <div key={m.id} className="flex items-center gap-3 px-3 py-2 border-b border-[var(--border-muted)] last:border-b-0 bg-[var(--bg-card)]" data-testid={`admin-row-${m.id}`}>
                            <div className="w-32 text-[10px] uppercase tracking-widest font-bold text-[var(--text-muted)]">{fmtTime(m.kickoff, lang)}</div>
                            <div className="flex-1 min-w-0 text-sm text-[var(--text-primary)] truncate">
                                <span className="text-[var(--text-muted)] text-xs mr-2">{m.round}</span>
                                {teams[m.home_team_id]?.name || "TBD"} <span className="text-[var(--text-muted)]">vs</span> {teams[m.away_team_id]?.name || "TBD"}
                            </div>
                            <div className="font-mono text-sm text-[var(--text-primary)] tabular-nums w-12 text-right">
                                {m.home_goals != null ? `${m.home_goals}-${m.away_goals}` : "—"}
                            </div>
                            <button onClick={() => openEdit(m)} data-testid={`admin-edit-${m.id}`}
                                className="text-[10px] font-extrabold uppercase tracking-widest text-[var(--accent-text)] bg-[var(--accent-hex)] hover:bg-[var(--accent-hex-hover)] px-2 py-1 rounded">
                                {t("admin.edit_btn")}
                            </button>
                        </div>
                    ))}
                </div>
            </div>

            {editingMatch && (
                <div className="fixed inset-0 z-50 bg-black/70 flex items-center justify-center px-4 pt-24 pb-8 overflow-y-auto" data-testid="admin-edit-modal" onClick={() => setEditingMatch(null)}>
                    <div onClick={(e) => e.stopPropagation()} className="w-full max-w-md rounded-lg border border-[var(--border-base)] bg-[var(--bg-card)] p-6 space-y-4">
                        <h3 className="font-heading text-2xl font-bold uppercase text-[var(--text-primary)]">{t("admin.modal.set_result")}</h3>
                        <p className="text-sm text-[var(--text-secondary)]">
                            {teams[editingMatch.home_team_id]?.name} vs {teams[editingMatch.away_team_id]?.name}
                        </p>
                        <div className="flex items-center justify-center gap-2 py-4">
                            <ScoreStepper testid="admin-result-home" value={hg} onChange={setHg} />
                            <span className="text-[var(--text-muted)] font-heading text-2xl px-2">:</span>
                            <ScoreStepper testid="admin-result-away" value={ag} onChange={setAg} />
                        </div>
                        {editingMatch.stage === "knockout" && hg === ag && (
                            <div className="p-3 rounded-md border border-[#FFB800]/40 bg-[#FFB800]/10" data-testid="admin-penalty-block">
                                <div className="text-[10px] uppercase tracking-widest font-bold text-[#FFB800] mb-2">{t("admin.modal.penalty_title")}</div>
                                <p className="text-xs text-[var(--text-secondary)] mb-2">{t("admin.modal.penalty_hint")}</p>
                                <div className="flex gap-2">
                                    {[editingMatch.home_team_id, editingMatch.away_team_id].filter(Boolean).map((tid) => (
                                        <button
                                            key={tid}
                                            type="button"
                                            onClick={() => setPenWinner(tid)}
                                            data-testid={`pen-winner-${tid}`}
                                            className={`flex-1 px-3 py-2 rounded-md text-xs font-extrabold uppercase tracking-widest border ${
                                                penWinner === tid
                                                    ? "bg-[#FFB800] text-black border-[#FFB800]"
                                                    : "bg-transparent text-[var(--text-primary)] border-[var(--border-hover)]"
                                            }`}>
                                            {teams[tid]?.name || "?"}
                                        </button>
                                    ))}
                                </div>
                            </div>
                        )}
                        <div className="flex gap-2">
                            <button onClick={() => setEditingMatch(null)} className="flex-1 border border-[var(--border-hover)] text-[var(--text-primary)] font-bold uppercase tracking-wide px-4 py-2.5 rounded-md">{t("admin.modal.cancel")}</button>
                            <button onClick={saveResult} data-testid="admin-save-result" className="flex-1 bg-[var(--accent-hex)] text-[var(--accent-text)] font-extrabold uppercase tracking-wide px-4 py-2.5 rounded-md hover:bg-[var(--accent-hex-hover)]">{t("admin.modal.save")}</button>
                        </div>
                    </div>
                </div>
            )}

            {/* Danger zone: delete pool */}
            {onDeletePool && (
                <div className="mt-6 rounded-lg border border-red-500/30 bg-red-500/5 p-4" data-testid="admin-danger-zone">
                    <div className="flex items-start justify-between gap-3 flex-wrap">
                        <div>
                            <h3 className="font-heading text-lg font-extrabold uppercase text-red-400">{t("pool.admin.danger.title")}</h3>
                            <p className="text-xs text-[var(--text-secondary)] mt-1 max-w-lg">{t("pool.admin.danger.desc")}</p>
                        </div>
                        <button type="button" onClick={onDeletePool} data-testid="admin-delete-pool-btn"
                            className="inline-flex items-center gap-2 bg-red-600 hover:bg-red-700 text-white font-extrabold uppercase tracking-wide text-xs px-4 py-2.5 rounded-md">
                            <Trash2 className="h-4 w-4" />
                            {t("pool.admin.delete.button")}
                        </button>
                    </div>
                </div>
            )}
        </div>
    );
}
